/**
 * RolePill — clickable role badge for the admin user list.
 *
 * Tap opens a tiny menu with role options. Picking one POSTs to
 * /api/admin/users/{user_id}/set-role and calls onChange(newRole) so the
 * parent can patch its local state (no full refetch needed).
 *
 *   - Admin viewers can change anyone except other admins + themselves.
 *   - Co-admin viewers see a static read-only badge (no menu).
 *   - The current user's own role is read-only (we don't allow self-demote).
 *
 * Spec: Jun 4 2026, requested from the admin user list to make role changes
 * one tap rather than open-modal-confirm-promote.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Role → display colour palette. Mirrors the swatches we already use on
// the user cards so the pills feel native to the list.
const ROLE_STYLES = {
  admin:      'bg-red-600/20 text-red-300 border-red-500/40',
  king:       'bg-amber-600/20 text-amber-300 border-amber-500/40',
  coadmin:    'bg-cyan-600/20 text-cyan-300 border-cyan-500/40', // legacy
  ambassador: 'bg-pink-600/20 text-pink-300 border-pink-500/40',
  writer:     'bg-purple-600/20 text-purple-300 border-purple-500/40',
  pending_writer: 'bg-amber-600/20 text-amber-300 border-amber-500/40',
  user:       'bg-blue-600/20 text-blue-300 border-blue-500/40',
};

// Display labels — keep them short so the pill width matches the
// existing badges. Feb 12 2026: "Admin" relabelled to "High King"
// per user spec (role name `admin` stays for compatibility).
const ROLE_LABELS = {
  admin: 'HIGH KING',
  king: 'KING',
  coadmin: 'KING',  // legacy rows display as KING after Feb 12 2026 rename
  ambassador: 'AMBASSADOR',
  writer: 'WRITER',
  pending_writer: 'RETAIN PENDING',
  user: 'USER',
};

// Options offered when the pill is tapped. Order matches the typical
// promotion path (reader → writer → king). Ambassador appointment
// happens via the King dashboard, not this dropdown.
const PICKABLE_ROLES = ['user', 'writer', 'king', 'pending_writer'];

const RolePill = ({
  user,                  // user row from the admin list
  viewerRole = 'admin',  // 'admin' | 'coadmin'
  selfUserId,            // logged-in admin's own user_id
  onChange,              // (newRole) => void — let parent patch list state
  testIdSuffix = '',     // appended to data-testid so mobile/desktop stay unique
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef(null);

  const role = (user.role || 'user').toLowerCase();
  const label = ROLE_LABELS[role] || role.toUpperCase();
  const palette = ROLE_STYLES[role] || ROLE_STYLES.user;

  // Co-admin viewers, admin targets, and self-targets are read-only.
  const interactive = (
    viewerRole === 'admin'
    && role !== 'admin'
    && user.user_id !== selfUserId
  );

  // Close the menu when clicking outside or pressing Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = async (next) => {
    if (next === role) { setOpen(false); return; }
    setBusy(true);
    const tid = toast.loading(`Setting role to ${ROLE_LABELS[next] || next}…`);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/users/${user.user_id}/set-role`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: next }),
        },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Role change failed');
      toast.success(`Role: ${ROLE_LABELS[next] || next}`, { id: tid });
      onChange?.(next);
    } catch (e) {
      toast.error(e.message || 'Role change failed', { id: tid });
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  if (!interactive) {
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold border ${palette}`}
        data-testid={`role-pill-${user.user_id}${testIdSuffix}`}
      >
        {label}
      </span>
    );
  }

  return (
    <span ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className={`px-2 py-1 rounded-full text-xs font-semibold border transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${palette} ${busy ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}
        data-testid={`role-pill-${user.user_id}${testIdSuffix}`}
        title="Tap to change role"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {busy ? <Loader2 className="w-3 h-3 inline-block animate-spin mr-1" /> : null}
        {label}
        <span className="ml-1 opacity-60">▾</span>
      </button>
      {open && (
        <span
          role="menu"
          className="absolute z-50 left-0 top-full mt-1 min-w-[160px] rounded-lg bg-slate-900 border border-slate-700 shadow-2xl py-1"
          data-testid={`role-pill-menu-${user.user_id}${testIdSuffix}`}
        >
          {PICKABLE_ROLES.map((opt) => {
            const active = opt === role;
            return (
              <button
                key={opt}
                type="button"
                role="menuitem"
                onClick={() => pick(opt)}
                disabled={active}
                className={`w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center justify-between gap-2 transition ${
                  active
                    ? 'text-slate-500 cursor-default'
                    : 'text-white hover:bg-slate-800'
                }`}
                data-testid={`role-pill-option-${opt}-${user.user_id}${testIdSuffix}`}
              >
                <span className={`px-2 py-0.5 rounded-full border ${ROLE_STYLES[opt]}`}>
                  {ROLE_LABELS[opt]}
                </span>
                {active && <span className="text-[10px] uppercase tracking-widest text-slate-500">current</span>}
              </button>
            );
          })}
        </span>
      )}
    </span>
  );
};

export default RolePill;
